import {
  useMemo,
  useState,
  useCallback,
  useEffect
} from "react";
import { useMsal } from "@azure/msal-react";
import { type IdentityProvider } from "@/types/core";
import { authLogger } from "@/utilities/auth-logger";
import { config } from "@/config/environment";
import {
  createLogoutRequest,
  clearActiveAccount
} from "@/utilities/msal-utils";
import { useMsalToken } from "./use-msal-token";
import { type UserInfo } from "@/types/core";
import { getRedirectLoginRequest } from "@/config/auth-config";
import { MsalService } from "@/components/security/msal-service";

// ============================================================================
// USE AUTH HOOK
// ============================================================================

export function useAuth() {
  const { instance, accounts } = useMsal();
  const { ensureValidToken } = useMsalToken();
  const [isLoading, setIsLoading] = useState(false);
  const [userInfoWithAccessToken, setUserInfoWithAccessToken] =
    useState<UserInfo | null>(null);
  const [lastAccountId, setLastAccountId] = useState<string | null>(null);

  // Get centralized MSAL service
  const msalService = useMemo(
    () => MsalService.getInstance(instance),
    [instance]
  );

  // Check for accounts on mount. The user-info effect below already clears
  // state when the account list is empty.
  useEffect(() => {
    if (accounts.length === 0) {
      authLogger.debug("No accounts found in MSAL cache");
    }
  }, [accounts.length]);

  // Effect to acquire access token and extract user info with it
  useEffect(() => {
    const acquireTokenAndExtractUserInfo = async () => {
      if (accounts.length === 0) {
        setUserInfoWithAccessToken(null);
        return;
      }

      try {
        const account = accounts[0];
        const userInfo = await msalService.getUserInfoWithAccessToken(account);
        setUserInfoWithAccessToken(userInfo);

        if (config.debug.enabled) {
          authLogger.roleExtraction("User info extracted", {
            account: account.username,
            email: userInfo.email,
            userId: userInfo.userId
          });
        }
      } catch (error) {
        authLogger.authError(error, "acquireTokenAndExtractUserInfo");
        setUserInfoWithAccessToken(null);
      }
    };

    acquireTokenAndExtractUserInfo();
  }, [accounts, msalService]);

  const userInfo = useMemo((): UserInfo | null => {
    if (accounts.length === 0) return null;
    return userInfoWithAccessToken;
  }, [accounts, userInfoWithAccessToken]);

  // Effect to handle token refresh when needed
  useEffect(() => {
    if (accounts.length > 0) {
      const account = accounts[0];
      // Only refresh token if we have a new account
      if (account.homeAccountId !== lastAccountId) {
        if (config.debug.enabled) {
          console.log(
            "🔄 useAuth: Refreshing token for new account:",
            account.username
          );
        }
        ensureValidToken(account).then((success) => {
          if (success) {
            setLastAccountId(account.homeAccountId);
          }
        });
      }
    }
  }, [accounts, msalService, lastAccountId, ensureValidToken]); // Add ensureValidToken to dependencies

  const isAuthenticated = useMemo(() => {
    return accounts.length > 0 || msalService.getActiveAccount() !== null;
  }, [accounts, msalService]);

  const activeAccount = useMemo(() => {
    return (
      msalService.getActiveAccount() ??
      (accounts.length > 0 ? accounts[0] : null)
    );
  }, [msalService, accounts]);

  const login = useCallback(
    async (email: string, provider: IdentityProvider) => {
      setIsLoading(true);

      try {
        // First try to find an existing account for this email
        const existingAccount = instance
          .getAllAccounts()
          .find(
            (account) =>
              account.username === email ||
              account.homeAccountId.includes(email)
          );

        if (existingAccount) {
          // If we have an existing account, try silent token acquisition
          try {
            authLogger.info("Attempting silent login for existing account", {
              username: existingAccount.username
            });

            // Set the active account
            msalService.setActiveAccount(existingAccount);

            // Get user info with access token
            const userInfo = await msalService.getUserInfoWithAccessToken(
              existingAccount
            );
            setUserInfoWithAccessToken(userInfo);

            // Start session
            msalService.startSession(existingAccount);

            authLogger.info("Silent login successful");
            return;
          } catch (silentError) {
            authLogger.warn("Silent login failed, trying interactive login", {
              error: String(silentError)
            });
            // Continue to interactive login
          }
        }

        // Use MSAL interactive login with pre-filled email for Entra ID user flows
        authLogger.info("Starting interactive login", {
          email,
          provider
        });

        await instance.loginRedirect(getRedirectLoginRequest(email, provider));

        // Note: loginRedirect doesn't return a result immediately
        // The redirect will happen and the user will be redirected back to the app
        // The account will be set during the redirect handling in use-msal-init.ts

        authLogger.redirect("MSAL redirect authentication initiated");
      } catch (error) {
        authLogger.authError(error, "login");

        // Provide more specific error messages
        let errorMessage = "Login failed. Please try again.";
        if (error instanceof Error) {
          if (error.message.includes("popup")) {
            errorMessage = "Popup blocked. Please allow popups and try again.";
          } else if (error.message.includes("redirect")) {
            errorMessage = "Redirect failed. Please try again.";
          } else if (error.message.includes("network")) {
            errorMessage =
              "Network error. Please check your connection and try again.";
          }
        }

        throw new Error(errorMessage, { cause: error });
      } finally {
        setIsLoading(false);
      }
    },
    [instance, msalService]
  );

  /**
   * Logs out the user with proper cleanup
   * @param options - Logout options
   */
  const logout = async (options?: {
    clearCache?: boolean;
    seamless?: boolean;
  }) => {
    const { clearCache = true, seamless = true } = options || {};

    try {
      authLogger.info("Starting logout process", { seamless });

      // Get the current active account for seamless logout
      const activeAccount = msalService.getActiveAccount();

      // Clear the active account
      clearActiveAccount(instance);

      // Clear any local storage or session storage if needed
      if (clearCache) {
        // Clear any application-specific cache
        sessionStorage.removeItem("tempii-user-preferences");
        localStorage.removeItem("tempii-ui-preferences");
      }

      if (seamless && activeAccount) {
        // Perform seamless logout with account context to avoid account selection prompt
        const logoutRequest = createLogoutRequest();
        await instance.logoutRedirect({
          ...logoutRequest,
          account: activeAccount
        });
        authLogger.info("Seamless logout completed successfully");
      } else {
        // Fallback to standard logout
        await instance.logoutRedirect(createLogoutRequest());
        authLogger.info("Standard logout completed successfully");
      }
    } catch (error) {
      authLogger.authError(error, "logout");
      // Even if logout fails, we should clear local state
    }
  };

  /**
   * Logs out the user and redirects to login page
   */
  const logoutAndRedirect = async () => {
    try {
      await logout({ clearCache: true, seamless: true });
    } catch (error) {
      authLogger.authError(error, "logoutAndRedirect");
      // Force redirect to login even if logout fails
      window.location.replace("/login");
    }
  };

  /**
   * Get user info with access token for better email extraction
   */
  const getUserInfoWithAccessToken =
    useCallback(async (): Promise<UserInfo | null> => {
      if (accounts.length === 0) return null;

      // If we already have enhanced user info, return it
      if (userInfoWithAccessToken) {
        return userInfoWithAccessToken;
      }

      try {
        const account = accounts[0];
        return await msalService.getUserInfoWithAccessToken(account);
      } catch (error) {
        authLogger.authError(error, "getUserInfoWithAccessToken");
        return userInfo; // Fallback to regular userInfo
      }
    }, [accounts, userInfo, userInfoWithAccessToken, msalService]);

  /**
   * Refreshes the access token silently.
   * Use `forceRefresh` after Entra group membership changes so the next API
   * calls carry role claims that were not on the cached token.
   */
  const refreshAccessToken = useCallback(async (options?: {
    forceRefresh?: boolean;
  }): Promise<{
    success: boolean;
    error?: string;
  }> => {
    try {
      const activeAccount = msalService.getActiveAccount();
      if (!activeAccount) {
        return {
          success: false,
          error: "No active account found"
        };
      }

      if (config.debug.enabled) {
        authLogger.info("Refreshing access token", {
          account: activeAccount.username,
          forceRefresh: options?.forceRefresh === true
        });
      }

      const success = await msalService.refreshToken(activeAccount, options);

      if (success) {
        // Update user info with fresh token
        const userInfo = await msalService.getUserInfoWithAccessToken(
          activeAccount
        );
        setUserInfoWithAccessToken(userInfo);

        if (config.debug.enabled) {
          authLogger.info("Access token refreshed successfully");
        }
      }

      return { success };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";

      if (config.debug.enabled) {
        authLogger.error("Token refresh failed", { error: errorMessage });
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }, [msalService]);

  return {
    activeAccount,
    userInfo,
    getUserInfoWithAccessToken,
    isAuthenticated,
    login,
    logout,
    logoutAndRedirect,
    refreshAccessToken,
    isLoading: isLoading
  };
}
